import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function InstantActionsMenu({ user, examScheduleMasterId, onMessage }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleClose = () => setAnchorEl(null);

  const handleGenerateInstant = async () => {
    setLoadingGenerate(true);
    handleClose();

    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/exam-halltickets/instant/generate`, {
        clientId: user.client_id,
        collegeId: user.college_id,
        masterScheduleId: examScheduleMasterId,
        created_by: user.user_email,
      });

      onMessage(data.message || "Instant exam halltickets generated successfully", "success");
    } catch (err) {
      onMessage(
        err?.response?.data?.message || "Failed to generate instant exam halltickets",
        "error"
      );
    } finally {
      setLoadingGenerate(false);
    }
  };

  const handleDownloadInstant = async () => {
    setLoadingDownload(true);
    setProgress(0);
    handleClose();

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/exam-halltickets/instant/download`,
        {
          clientId: user.client_id,
          collegeId: user.college_id,
          masterScheduleId: examScheduleMasterId,
        },
        {
          responseType: "blob",
          onDownloadProgress: (event) => {
            if (event.total) {
              setProgress(Math.round((event.loaded * 100) / event.total));
            }
          },
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Instant_Halltickets_${examScheduleMasterId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      onMessage(
        err?.response?.data?.message || "Failed to download instant exam halltickets",
        "error"
      );
    } finally {
      setLoadingDownload(false);
      setProgress(0);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ color: "#fff", textTransform: "none" }}
      >
        Actions
      </Button>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleGenerateInstant}>
          <ListItemIcon>
            <PrintIcon fontSize="small" sx={{ color: "green" }} />
          </ListItemIcon>
          <ListItemText
            primary="Generate Instant Exam Halltickets"
            primaryTypographyProps={{ fontSize: "0.9rem" }}
          />
        </MenuItem>
        <MenuItem onClick={handleDownloadInstant}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" sx={{ color: "orange" }} />
          </ListItemIcon>
          <ListItemText
            primary="Download Instant Exam Halltickets"
            primaryTypographyProps={{ fontSize: "0.9rem" }}
          />
        </MenuItem>
      </Menu>

      <Dialog open={loadingGenerate}>
        <DialogTitle>Generating Instant Halltickets...</DialogTitle>
        <DialogContent>
          <Box sx={{ width: 320 }}>
            <LinearProgress variant="indeterminate" />
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={loadingDownload}>
        <DialogTitle>Downloading Instant Halltickets...</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {progress > 0 ? `${progress}% completed` : "Starting download..."}
          </Typography>
          <Box sx={{ width: 320 }}>
            <LinearProgress
              variant={progress > 0 ? "determinate" : "indeterminate"}
              value={progress}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

InstantActionsMenu.propTypes = {
  user: PropTypes.object.isRequired,
  examScheduleMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onMessage: PropTypes.func.isRequired,
};
